import React from 'react';
import { is, OrderedSet } from 'immutable';
import { v4 as uuidv4 } from 'uuid';
import { LIGHT_SELECTION_COLORS } from 'components/common/DraftEditor/util/helpers';

const EMPTY_SET = new OrderedSet();

// Possible block types
export const BLOCK_TYPE = {
  // This is used to represent a normal text block (paragraph).
  UNSTYLED: 'unstyled',
  HEADER_ONE: 'header-one',
  HEADER_TWO: 'header-two',
  HEADER_THREE: 'header-three',
  HEADER_FOUR: 'header-four',
  HEADER_FIVE: 'header-five',
  HEADER_SIX: 'header-six',
  UNORDERED_LIST_ITEM: 'unordered-list-item',
  ORDERED_LIST_ITEM: 'ordered-list-item',
  BLOCKQUOTE: 'blockquote',
  PULLQUOTE: 'pullquote',
  CODE: 'code-block',
  ATOMIC: 'atomic',
  TABLE_ROW_ITEM: 'table-row-item',
};

// Possible inline block types
export const INLINE_TYPE = {
  HYPERLINK: 'hyperlink',
};

export const STYLE_MAP = {
  CODE: 'pre',
  BOLD: 'strong',
  ITALIC: 'em',
  UNDERLINE: 'u',
};

export const CUSTOM_STYLE_MAP = {
  STRIKETHROUGH: {
    textDecoration: 'line-through',
  },
  ...LIGHT_SELECTION_COLORS,
};

// Returns additional block wrapper
export const getBlockWrapper = (blockType) => {
  switch (blockType) {
    case BLOCK_TYPE.UNORDERED_LIST_ITEM:
      return 'ul';
    case BLOCK_TYPE.ORDERED_LIST_ITEM:
      return 'ol';
    case BLOCK_TYPE.CODE:
      return 'pre';
    case BLOCK_TYPE.BLOCKQUOTE:
      return 'div';
    case BLOCK_TYPE.TABLE_ROW_ITEM:
      return 'table';
    default:
      return null;
  }
};

// The reason this returns an array is because a single block might get wrapped
// in two tags.
export const getTags = (blockType) => {
  switch (blockType) {
    case BLOCK_TYPE.HEADER_ONE:
      return ['h1'];
    case BLOCK_TYPE.HEADER_TWO:
      return ['h2'];
    case BLOCK_TYPE.HEADER_THREE:
      return ['h3'];
    case BLOCK_TYPE.HEADER_FOUR:
      return ['h4'];
    case BLOCK_TYPE.HEADER_FIVE:
      return ['h5'];
    case BLOCK_TYPE.HEADER_SIX:
      return ['h6'];
    case BLOCK_TYPE.UNORDERED_LIST_ITEM:
    case BLOCK_TYPE.ORDERED_LIST_ITEM:
      return ['li'];
    case BLOCK_TYPE.BLOCKQUOTE:
      return ['blockquote'];
    case BLOCK_TYPE.CODE:
      return ['code'];
    case BLOCK_TYPE.ATOMIC:
      return ['figure'];
    case BLOCK_TYPE.TABLE_ROW_ITEM:
      return ['td'];
    default:
      return [];
  }
};

export const canHaveDepth = (blockType) => {
  switch (blockType) {
    case BLOCK_TYPE.UNORDERED_LIST_ITEM:
    case BLOCK_TYPE.ORDERED_LIST_ITEM:
    case BLOCK_TYPE.TABLE_ROW_ITEM:
      return true;
    default:
      return false;
  }
};

export const getEntityRanges = (text, charMetaList) => {
  let charEntity = null;
  let prevCharEntity = null;
  let ranges = [];
  let rangeStart = 0;

  for (let i = 0, len = text.length; i < len; i++) {
    prevCharEntity = charEntity;
    let meta = charMetaList.get(i);
    charEntity = meta ? meta.getEntity() : null;
    if (i > 0 && charEntity !== prevCharEntity) {
      ranges.push([prevCharEntity, getStyleRanges(text.slice(rangeStart, i), charMetaList.slice(rangeStart, i))]);
      rangeStart = i;
    }
  }
  ranges.push([charEntity, getStyleRanges(text.slice(rangeStart), charMetaList.slice(rangeStart))]);
  return ranges;
};

const getStyleRanges = (text, charMetaList) => {
  let charStyle = EMPTY_SET;
  let prevCharStyle = EMPTY_SET;
  let ranges = [];
  let rangeStart = 0;
  for (let i = 0, len = text.length; i < len; i++) {
    prevCharStyle = charStyle;
    let meta = charMetaList.get(i);
    charStyle = meta ? meta.getStyle() : EMPTY_SET;
    if (i > 0 && !is(charStyle, prevCharStyle)) {
      ranges.push([text.slice(rangeStart, i), prevCharStyle]);
      rangeStart = i;
    }
  }
  ranges.push([text.slice(rangeStart), charStyle]);
  return ranges;
};

export const setupContentKeys = (content) => {
  if (Array.isArray(content)) {
    return content.map((fragment) => {
      return setupContentKeys(fragment);
    });
  }
  if (typeof content === 'object') {
    return content;
  }

  const key = uuidv4().substring(0, 5);
  return <span key={key}>{content}</span>;
};
